import { SignInResult } from "@/repository/models";
import * as Sentry from "@sentry/react";
import { envVariable, Variable } from "../constants/envVariable";

export function captureError(
	ex: Error | string,
	message?: string,
	tags?: Partial<Record<SentryTagKey, string>>,
	data?: { [key: string]: any }
) {
	Sentry.withScope((scope) => {
		if (tags) {
			const mappedTags = Object.fromEntries(
				Object.entries(tags).map(([key, value]) => [
					SentryTags[key as SentryTagKey],
					value,
				])
			);

			scope.setTags(mappedTags);
		}

		if (message) {
			if (typeof ex === "string") {
				ex = `${ex}: ${message}`;
			} else if (ex && typeof ex === "object") {
				ex.message = ex.message ? `${ex.message}: ${message}` : message;
				ex.name = ex.name ? `${ex.name}: ${message}` : message;
			}

			scope.setExtra(SentryExtras.additionalData, data);
		}

		scope.captureException(typeof ex === "string" ? new Error(ex) : ex);
	});
}

export function addBreadcrumb(
	category: SentryBreadcrumbsCategory,
	message: string,
	data?: any,
	level: Sentry.SeverityLevel = "info"
) {
	Sentry.addBreadcrumb({
		category,
		message,
		level,
		data,
	});
}

export function captureEvent(
	message: string,
	data?: any,
	level: Sentry.SeverityLevel = "info"
) {
	Sentry.captureEvent({
		message,
		level,
		extra: data,
	});
}

export function setSentryUserDetails(userDetails: SignInResult) {
	Sentry.setUser({
		id: userDetails.userId,
		email: userDetails.upn,
	});

	Sentry.setTag("tenant", userDetails.tenantId);
}

export function initializeSentry() {
	const sentry = envVariable(Variable.Sentry) as SentryConfiguration;

	if (!sentry) {
		console.warn("Sentry configuration missing");

		return;
	}

	Sentry.init({
		dsn: sentry.dsn,
		release: sentry.release,
		environment: sentry.environment,
		integrations: [
			Sentry.browserTracingIntegration(),
			Sentry.replayIntegration({
				maskAllText: sentry.replaysMaskText === "true",
				blockAllMedia: sentry.replaysBlockMedia === "true",
			}),
		],
		tracesSampleRate: parseFloat(sentry.tracesSampleRate),
		tracePropagationTargets: [sentry.tracePropagationTarget],
		replaysSessionSampleRate: parseFloat(sentry.replaysSessionSampleRate),
		replaysOnErrorSampleRate: parseFloat(sentry.replaysOnErrorSampleRate),
	});
}

type SentryBreadcrumbsCategory = "ui_interaction" | "websocket_event";

export const SentryTags = {
	interactionId: "interaction_id",
	userId: "user_id",
	draftMessageId: "draft_message_id",
	interactionType: "interaction_type",
} as const;

export type SentryTagKey = keyof typeof SentryTags;

export const SentryExtras = {
	additionalMessage: "additional_message",
	additionalData: "additional_data",
} as const;

export interface SentryConfiguration {
	dsn: string;
	release: string;
	environment: string;
	replaysMaskText: string;
	replaysBlockMedia: string;
	tracesSampleRate: string;
	tracePropagationTarget: string;
	replaysSessionSampleRate: string;
	replaysOnErrorSampleRate: string;
	requestFeedbackOnError: string;
}

export type SentryExtraKey = keyof typeof SentryExtras;
